import { PRIMARY } from "@constants/colors"
import { AdminOperatorRunChat } from "@domain/ai_operator"
import useTranslate from "@hooks/use_translate"
import {
  Box,
  Button,
  LinearProgress,
  List,
  ListItemButton,
  Stack,
  Typography
} from "@mui/material"
import dayjs from "dayjs"

import { Dot } from "./RunStatus"
import { runTone } from "./operatorRun.utils"

type Props = {
  items: AdminOperatorRunChat[]
  selectedChatId?: string
  loading: boolean
  hasMore: boolean
  loadingMore: boolean
  onSelect: (chatId: string) => void
  onLoadMore: () => void
}

const listTime = (value: string) => {
  const time = dayjs(value)
  if (time.isSame(dayjs(), "day")) return time.format("HH:mm")
  if (time.isSame(dayjs(), "year")) return time.format("DD.MM HH:mm")
  return time.format("DD.MM.YYYY")
}

function Row({
  chat,
  selected,
  onSelect
}: {
  chat: AdminOperatorRunChat
  selected: boolean
  onSelect: () => void
}) {
  const { lastRun } = chat
  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{
        display: "block",
        py: 1,
        borderLeft: "3px solid transparent",
        "&.Mui-selected": {
          borderLeftColor: PRIMARY,
          bgcolor: `${PRIMARY}1f`
        }
      }}
    >
      <Stack direction="row" gap={1} alignItems="center">
        <Dot tone={chat.running ? "info" : runTone(lastRun)} />
        <Typography variant="body2" fontWeight={600} noWrap flex={1}>
          {lastRun.userName ?? lastRun.cPersonId}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          flexShrink={0}
          title={dayjs(lastRun.createdAt).format("DD.MM.YYYY HH:mm:ss")}
        >
          {listTime(lastRun.createdAt)}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        display="block"
      >
        {lastRun.customerName ?? lastRun.customerId}
      </Typography>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography variant="caption" noWrap flex={1}>
          {lastRun.request}
        </Typography>
        {chat.failed ? (
          <Typography variant="caption" color="error.main" flexShrink={0}>
            {`${chat.failed}✕`}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" flexShrink={0}>
          {chat.runs}
        </Typography>
      </Stack>
    </ListItemButton>
  )
}

export default function OperatorChatList(props: Props) {
  const { translate } = useTranslate()
  return (
    <Box sx={{ flex: 1, overflow: "auto", position: "relative" }}>
      {props.loading ? (
        <Box sx={{ position: "sticky", top: 0, zIndex: 1 }}>
          <LinearProgress />
        </Box>
      ) : null}
      <List disablePadding>
        {props.items.map((chat) => (
          <Row
            key={chat.chatId}
            chat={chat}
            selected={chat.chatId === props.selectedChatId}
            onSelect={() => props.onSelect(chat.chatId)}
          />
        ))}
      </List>
      {!props.loading && !props.items.length ? (
        <Typography
          variant="body2"
          color="text.secondary"
          p={3}
          textAlign="center"
        >
          {translate("ai_operator_empty")}
        </Typography>
      ) : null}
      {props.hasMore ? (
        <Stack p={1}>
          <Button
            size="small"
            disabled={props.loadingMore}
            onClick={props.onLoadMore}
          >
            {translate("ai_operator_load_more")}
          </Button>
        </Stack>
      ) : null}
    </Box>
  )
}
